// ========================================
// components/admin/AuditLogs.js - Admin Audit Log Viewer
// ========================================
// PURPOSE: Allow administrators to view and search audit logs
// - Displays user actions with timestamps and IP addresses
// - Filter by action type and user email
// - Pagination for large log sets
// - Mobile-responsive using App.css classes
// ========================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AuditLogs = () => {
  // ========================================
  // STATE VARIABLES
  // ========================================
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [filterAction, setFilterAction] = useState('');
  const [filterUserEmail, setFilterUserEmail] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const logsPerPage = 50;

  // ========================================
  // GET ADMIN TOKEN
  // ========================================
  const getToken = () => localStorage.getItem('adminToken');

  // ========================================
  // FETCH AUDIT LOGS FROM BACKEND
  // ========================================
  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = getToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/audit-logs`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          page: currentPage,
          limit: logsPerPage,
          action: filterAction,
          userEmail: filterUserEmail
        }
      });
      
      setLogs(response.data.logs);
      setTotalPages(response.data.pages || 1);
      setTotalLogs(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError('Failed to load audit logs. Please try again.');
      if (err.response?.status === 401) {
        window.location.href = '/admin/login';
      }
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // FETCH LOGS WHEN PAGE OR FILTERS CHANGE
  // ========================================
  useEffect(() => {
    fetchLogs();
  }, [currentPage, filterAction, filterUserEmail]);

  // ========================================
  // RESET PAGE WHEN FILTERS CHANGE
  // ========================================
  useEffect(() => {
    setCurrentPage(1);
  }, [filterAction, filterUserEmail]);

  // ========================================
  // CLEAR ALL FILTERS
  // ========================================
  const clearFilters = () => {
    setFilterAction('');
    setFilterUserEmail('');
    setCurrentPage(1);
  };

  // ========================================
  // FORMAT ACTION FOR DISPLAY
  // ========================================
  const formatAction = (action) => {
    return action.replace(/_/g, ' ').toLowerCase();
  };

  // ========================================
  // GET CSS CLASS FOR ACTION TYPE
  // ========================================
  const getActionClass = (action) => {
    if (action.includes('SUCCESS') || action === 'CHECK_IN_COMPLETED') {
      return 'audit-badge-success';
    }
    if (action.includes('FAILED')) {
      return 'audit-badge-danger';
    }
    if (action.includes('DELETED')) {
      return 'audit-badge-warning';
    }
    if (action.includes('VIEWED') || action.includes('DOWNLOADED')) {
      return 'audit-badge-info';
    }
    return 'audit-badge-default';
  };

  // ========================================
  // AVAILABLE ACTION FILTERS
  // ========================================
  const actionFilters = [
    { value: '', label: 'All Actions' },
    { value: 'LOGIN_SUCCESS', label: 'Login Success' },
    { value: 'LOGIN_FAILED', label: 'Login Failed' },
    { value: 'ASSET_CREATED', label: 'Asset Created' },
    { value: 'ASSET_DELETED', label: 'Asset Deleted' },
    { value: 'ASSET_VIEWED_BY_BENEFICIARY', label: 'Asset Viewed' },
    { value: 'DOCUMENT_DOWNLOADED', label: 'Document Downloaded' },
    { value: 'TRUSTEE_ACCEPTED', label: 'Trustee Accepted' },
    { value: 'TRUSTEE_VERIFIED_DEATH', label: 'Death Verified' },
    { value: 'CHECK_IN_COMPLETED', label: 'Check-In' },
    { value: 'PLAN_UPGRADED', label: 'Plan Upgraded' }
  ];

  // ========================================
  // RENDER LOADING STATE
  // ========================================
  if (loading && logs.length === 0) {
    return (
      <div className="admin-content">
        <div className="admin-card">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading audit logs...</p>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER MAIN CONTENT
  // ========================================
  return (
    <div className="admin-content">
      <div className="admin-card">
        
        {/* Card Header with Title and Filters */}
        <div className="audit-header">
          <h3 className="admin-card-title">Audit Logs</h3>
          <div className="audit-stats">
            <span className="audit-total-count">{totalLogs} total records</span>
          </div>
        </div>
        
        {/* Filter Bar */}
        <div className="audit-filters">
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="audit-filter-select"
          >
            {actionFilters.map(filter => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
          
          <input
            type="text"
            placeholder="Filter by email..."
            value={filterUserEmail}
            onChange={(e) => setFilterUserEmail(e.target.value)}
            className="audit-filter-input"
          />
          
          {(filterAction || filterUserEmail) && (
            <button onClick={clearFilters} className="audit-filter-clear">
              Clear Filters
            </button>
          )}
        </div>
        
        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}
        
        {/* Logs Table */}
        {logs.length === 0 ? (
          <div className="admin-empty-state">
            <img src="/images/dashboard/empty-state.png" alt="No logs" className="admin-empty-icon" />
            <p>No audit logs found</p>
          </div>
        ) : (
          <>
            <div className="audit-table-responsive">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Details</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td data-label="Date & Time">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td data-label="User">
                        <span className="audit-user-email">{log.userEmail}</span>
                      </td>
                      <td data-label="Action">
                        <span className={`audit-badge ${getActionClass(log.action)}`}>
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td data-label="Details">
                        {log.details || '—'}
                      </td>
                      <td data-label="IP Address">
                        <code className="audit-ip">{log.ipAddress || '—'}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="audit-pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="audit-page-btn"
                >
                  Previous
                </button>
                <span className="audit-page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="audit-page-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;